'use client';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Select } from './select';

/**
 * The table, with the three things every table in this product grew on its own.
 *
 * Every list screen in both apps arrived at the same needs (order by a column,
 * type to narrow, pick a value from a dropdown to filter) and each one solved
 * them again with its own `useState` and its own `.sort()`. They disagreed:
 * one sorted dates as strings, one lost the filter when the rows refreshed, one
 * showed an empty table with no way to tell "nothing matched" from "nothing
 * exists". That last one is the reason the count and the empty state are part of
 * this and not optional.
 *
 * Sorting, searching and filtering are computed, never stored: the rows are the
 * truth and this holds only the question being asked of them. A refresh that
 * brings new rows therefore keeps the reader's sort and filter, which is what
 * they expect and what the hand-rolled versions all got wrong.
 */

/**
 * Where in a list the rows on screen fall: "1–100 of 329", or "all 42" when
 * there is only one page of them.
 *
 * One component rather than a string built at each pager, because the figures
 * are set apart from the word between them — the numbers in the panel's own
 * mono face, tabular so the line does not shuffle as a page number grows a
 * digit — and four call sites each writing that markup is four places to
 * forget it. Exported so the client app's own pager uses this one too.
 */
export function PageRange({ from, to, total }: { from?: number; to?: number; total: number }) {
  return (
    <span className="pager-range">
      {from === undefined || to === undefined ? (
        /* One page of them: "1–17 of 17" is three numbers saying one thing,
           and "Showing all 1" is a sentence nobody writes about a single
           row. The figure, then the word, same shape as the ranged form. */
        <>
          <b>{total}</b>
          {total === 1 ? ' row' : ' rows'}
        </>
      ) : (
        <>
          <b>{`${from}–${to}`}</b>
          {' of '}
          <b>{total}</b>
        </>
      )}
    </span>
  );
}

export interface Column<T> {
  key: string;
  label: React.ReactNode;
  /**
   * What this cell is worth for sorting and searching. Numbers and dates sort
   * as numbers; everything else compares as text in the reader's locale.
   * Omitted means the column is drawn but never ordered or searched by: right
   * for a column of buttons, wrong for anything the reader might look for.
   */
  value?: (row: T) => string | number | Date | null | undefined;
  /** What the cell shows. Defaults to `value`, which is right for plain text. */
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
  width?: string;
  /** Off until a reader ticks it in Columns: there for the one who wants it,
   *  not taking room from everyone else. A saved choice wins. */
  quiet?: boolean;
  /**
   * One sentence saying what the column counts, shown when the pointer rests
   * on its heading. For a heading that is one word — "Load", "Waiting" — where
   * the word alone leaves the reader guessing whose load, and waiting for
   * what.
   */
  hint?: string;
  /**
   * Holds this column to its `width` whatever lands in it: the value is cut
   * with an ellipsis rather than allowed to widen the column.
   *
   * This is what keeps a table still. Column widths are measured from the
   * rows currently on screen, so one long name on page three moves every
   * column on page three — filter, page, or sort and the whole table steps
   * sideways under the reader's eyes. A width plus this on each column whose
   * content has no natural length (a name, an address, an email) is enough to
   * make the layout the same on every page, without handing the table over to
   * `table-layout: fixed`, which would take the sizing away from the columns
   * that are better off measuring their own content.
   *
   * Only for a column carrying one line of text. Pass the full value as a
   * `title` in `render` so what is cut is still readable.
   */
  clip?: boolean;
  /**
   * Makes this column's header narrow the table as well as order it: a
   * dropdown of these values, keeping the rows `match` accepts. Which column
   * the filter belongs to is then the one thing a reader never has to work
   * out.
   *
   * For a narrow table with one or two filters. A table wide enough to scroll
   * sideways wants the toolbar `filters` prop instead: a filter that scrolls
   * out of sight leaves a short table with no visible reason for being short,
   * and three or more dropdowns across the header row read as a form pasted
   * over the labels. The toolbar is also where a question no single column
   * answers belongs.
   */
  filter?: {
    options: Array<{ value: string; label: string }>;
    /** True when the row survives. Not called for the "all" choice. */
    match: (row: T, value: string) => boolean;
    /**
     * What the control says while nothing is ticked, when the column's own
     * name does not pluralise into a sentence — "By" would read "All bys".
     */
    allLabel?: string;
  };
}

/** The table's own question, for a list too long to hold in the browser. */
export interface RemoteQuery {
  q: string;
  sort: { key: string; direction: 'ascending' | 'descending' } | null;
  page: number;
  size: number;
}

export interface Filter<T> {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  /** True when the row survives this filter. Not called for the "all" choice. */
  match: (row: T, value: string) => boolean;
  /** What the control says while nothing is chosen, when neither the label nor
   * its plural is the sentence wanted ("Anybody", not "All bys"). */
  allLabel?: string;
  /**
   * Lets the reader tick several values, kept if the row matches any one of
   * them: `match` is still asked about one value at a time, so a filter reads
   * the same however many are chosen.
   */
  multiple?: boolean;
  /**
   * Plural of what this picks, for the trigger once more than one is ticked:
   * "2 types". Defaults to the label lowercased with an s, which is right for
   * most and wrong for the words it is wrong for.
   */
  manyLabel?: string;
}

type Direction = 'ascending' | 'descending';
type PageSize = 20 | 50 | 100 | 'all';
const PAGE_SIZES: PageSize[] = [20, 50, 100, 'all'];
/** Below this, a page selector is one more control for a list that fits on
 * screen without it; every table gets it the moment it actually needs it. */
const SMALLEST_PAGE = 20;
/** Long enough that a word typed into the search box is one navigation. */
const URL_SETTLE_MS = 350;

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  filters = [],
  searchable = false,
  searchLabel = 'Search',
  onRowClick,
  rowClassName,
  under,
  empty,
  noMatch = 'Nothing matches those filters.',
  initialSort,
  caption,
  fixedLayout = false,
  pager = true,
  pagerAt = 'both',
  urlKey,
  summary,
  actions,
  remote,
  loading = false,
  resetKeeps,
}: {
  rows: T[];
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;
  filters?: Array<Filter<T>>;
  searchable?: boolean;
  searchLabel?: string;
  onRowClick?: (row: T) => void;
  /** A class for the row itself, when the row's state is about the whole line
   *  rather than one cell: a revoked colleague, a cancelled run. */
  rowClassName?: (row: T) => string | undefined;
  /**
   * What sits under a row, across the full width: an account's models, a
   * case's links. Returning null — which is the default — leaves the table
   * exactly as it was.
   */
  under?: (row: T) => React.ReactNode;
  /** Shown when there is genuinely nothing, as opposed to nothing matching. */
  empty: React.ReactNode;
  noMatch?: React.ReactNode;
  initialSort?: { key: string; direction: Direction };
  /** What the table lists, for a reader who cannot see the page around it. */
  caption: string;
  /**
   * Column widths held to what each `Column.width` says (or an even split,
   * for one left unset) instead of fit to whatever the current page happens
   * to contain. Off by default because most tables here have one column
   * nobody gave a width and are better off sized to their own content; turn
   * it on for a paginated table where a short last page would otherwise
   * narrow every column and pull them out from under the reader's cursor.
   */
  fixedLayout?: boolean;
  /**
   * Off for a table that was never going to grow past a handful of rows:
   * the plan-cost comparison on /spend is two rows for the lifetime of the
   * page, and "Showing 1–2 of 2" plus a disabled Prev/Next next to it reads
   * as controls for a table that has nothing to control. Every other table
   * keeps the default: shown the moment it's non-empty, not once it grows
   * past a size some caller guessed at.
   */
  pager?: boolean;
  /**
   * Where the pager and the count go. `bottom` is for a page whose own header
   * already carries the figures: the whitelist counts its rows in tiles, and a
   * second count between those and the column headings is one control twice.
   */
  pagerAt?: 'both' | 'bottom';
  /**
   * Prefix for this table's own query parameters, for the rare page holding
   * two tables: without one they would read and write each other's sort.
   */
  urlKey?: string;
  /**
   * A word about the rows, at the far end of the toolbar: "24 scans" — the
   * figure a reader wants before asking the table anything. Ignored with no
   * toolbar. As a function it gets (rows after filters, all rows).
   */
  summary?: React.ReactNode | ((shown: number, total: number) => React.ReactNode);
  /**
   * The page's action over the rows — "Add domain" — at the far end of the
   * toolbar, level with the search box. Below the figures and beside the
   * controls it belongs to, instead of up in the page head where a reader
   * looking at the table had to go back up to find it.
   */
  actions?: React.ReactNode;
  /**
   * The server searches, sorts and pages: `rows` is the current page only and
   * `total` the whole list's size. `onQuery` hears every change, the typed
   * words held back a moment so a word is one request rather than one a key.
   */
  remote?: { total: number; filtering?: boolean; onQuery: (query: RemoteQuery) => void };
  /**
   * A request is out. With rows on screen they dim under a moving bar, so a
   * reader never mistakes the last answer for this one; with none, the table
   * says it is loading rather than showing its empty state.
   */
  loading?: boolean;
  /**
   * A way back to the table as it first opens: filters, search, sort, page and
   * columns all cleared. The address parameters named here survive it (the
   * screen's own tab, say). Shown only while something differs from that.
   */
  resetKeeps?: readonly string[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const param = (name: string) => (urlKey ? `${urlKey}.${name}` : name);

  // Read once, on mount. The URL seeds this state and is written back to as it
  // changes; it is not read again, because a filter is answered by the rows on
  // screen and re-deriving every choice from the address on every render buys
  // nothing but a chance to fight itself.
  const [sort, setSort] = useState<{ key: string; direction: Direction } | null>(() => {
    const key = params.get(param('sort'));
    if (!key) return initialSort ?? null;
    return { key, direction: params.get(param('dir')) === 'desc' ? 'descending' : 'ascending' };
  });
  const [query, setQuery] = useState(() => params.get(param('q')) ?? '');
  /** A list per filter, empty meaning "all of them", so one code path covers
   * both the single-choice filters and the several-choice ones. */
  const [chosen, setChosen] = useState<Record<string, string[]>>(() => {
    const seed: Record<string, string[]> = {};
    for (const [name, value] of params.entries()) {
      const prefix = param('f.');
      if (!name.startsWith(prefix) || !value) continue;
      seed[name.slice(prefix.length)] = value.split(',');
    }
    return seed;
  });
  const [pageSize, setPageSize] = useState<PageSize>(() => {
    const raw = params.get(param('size'));
    if (raw === 'all') return 'all';
    const size = Number(raw);
    return PAGE_SIZES.includes(size as PageSize) ? (size as PageSize) : SMALLEST_PAGE;
  });
  const [page, setPage] = useState(() => Math.max(1, Number(params.get(param('page'))) || 1));

  const shown = useMemo(() => {
    let result = rows;
    if (remote) return result;

    for (const filter of filters) {
      const values = chosen[filter.key] ?? [];
      // Any one of them, not all: ticking Agency and Solo asks for both kinds
      // of row, which is what a reader ticking two things means by it.
      if (values.length > 0) {
        result = result.filter((row) => values.some((value) => filter.match(row, value)));
      }
    }

    for (const column of columns) {
      if (!column.filter) continue;
      const values = chosen[columnFilterKey(column.key)] ?? [];
      if (values.length > 0) {
        result = result.filter((row) => values.some((value) => column.filter!.match(row, value)));
      }
    }

    if (query.trim()) {
      // Searched against what the columns are worth, not against the rendered
      // cell: a cell can be a chip reading "Removed" over a status of
      // `removed`, and a reader typing either should find the row.
      const needle = query.trim().toLowerCase();
      result = result.filter((row) =>
        columns.some((column) => {
          const raw = column.value?.(row);
          if (raw === null || raw === undefined) return false;
          const text = raw instanceof Date ? raw.toISOString() : String(raw);
          return text.toLowerCase().includes(needle);
        }),
      );
    }

    if (sort) {
      const column = columns.find((c) => c.key === sort.key);
      if (column?.value) {
        const sign = sort.direction === 'ascending' ? 1 : -1;
        // A copy: sorting the prop in place mutates whatever the caller holds,
        // and React then has no reason to believe anything changed.
        result = [...result].sort((a, b) => sign * compare(column.value!(a), column.value!(b)));
      }
    }

    return result;
  }, [rows, columns, filters, chosen, query, sort, remote]);

  const filtering =
    Boolean(query.trim()) ||
    Boolean(remote?.filtering) ||
    Object.values(chosen).some((values) => values.length > 0);

  const sizeNow = remote && pageSize === 'all' ? 100 : pageSize;
  const wide = useWideScroll();
  const ask = useRef(remote?.onQuery);
  ask.current = remote?.onQuery;
  const [asked, setAsked] = useState(query);
  useEffect(() => {
    if (!remote) return;
    const typing = setTimeout(() => setAsked(query), URL_SETTLE_MS);
    return () => clearTimeout(typing);
  }, [query, remote]);
  useEffect(() => {
    ask.current?.({ q: asked.trim(), sort, page, size: sizeNow === 'all' ? 100 : sizeNow });
  }, [asked, sort, page, sizeNow]);
  const count = remote ? remote.total : shown.length;
  const summaryNode = typeof summary === 'function' ? summary(count, remote ? remote.total : rows.length) : summary;
  /**
   * Which lanes this reader keeps: per person and per table, in the browser.
   * Not the URL — a colleague opening a link would inherit it — and read once
   * on mount, so a storage that throws or holds an older shape leaves the
   * table whole rather than blank.
   */
  const storeKey = `cols:${pathname}:${urlKey ?? caption}`;
  // Anything in the address beyond what a reset keeps is a filter, a search, a sort or a page.
  const changedFromDefault = Boolean(resetKeeps && [...params.keys()].some((key) => !resetKeeps.includes(key)));
  const [hiddenCols, setHiddenCols] = useState<string[]>(() => quietKeys(columns));
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(storeKey);
      const parsed = saved ? (JSON.parse(saved) as unknown) : null;
      setHiddenCols(Array.isArray(parsed) ? parsed.filter((one) => typeof one === 'string') : quietKeys(columns));
    } catch {
      setHiddenCols(quietKeys(columns));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the defaults are read once per table, not per render
  }, [storeKey]);

  function keepColumns(keys: string[]) {
    const next = columns.filter((one) => !keys.includes(one.key)).map((one) => one.key);
    setHiddenCols(next);
    try {
      window.localStorage.setItem(storeKey, JSON.stringify(next));
    } catch {
      /* A browser that refuses to remember still shows the choice this session. */
    }
  }

  /* A lane with no heading is a lane of buttons: it carries the row's actions
     and is not something to switch off. The first column is what a row is
     read by, so it stays too. */
  const optional = columns.filter((one, at) => at > 0 && one.label);
  const anchor = columns[0]?.label ? columns[0] : null;
  // What a row is read by, and its buttons, stay whatever an older saved choice says.
  const shownColumns = columns.filter((one) => !optional.includes(one) || !hiddenCols.includes(one.key));
  /* Offered where there is something to choose between: five lanes and up.
     Under that the picker is a control beside three headings, and a table
     that short fits every screen already. */
  const canPickColumns = optional.length >= 4;

  const hasToolbar = searchable || filters.length > 0 || canPickColumns;

  /**
   * The reader's question, kept in the address bar.
   *
   * A refresh, a link sent to a colleague, and the browser's own Back all used
   * to hand back an unfiltered first page: the state lived in this component
   * and died with it. Written rather than read after mount (see the state
   * above), and with `replace`, so a table is one entry in the history however
   * many times its filter is changed rather than a wall of them to get past.
   *
   * Only what has been chosen is written. A default carried in the URL would
   * mean every table's address grew five parameters the moment it was opened,
   * and the first thing anyone did with such a link was delete half of it.
   */
  const settled = useRef(false);
  const search = params.toString();
  useEffect(() => {
    /* Built at the moment of writing, from the address as it then is: a screen
       may carry its own filters beside this table, and a URL assembled from
       what this table saw when it rendered puts back one without them. */
    const build = () => {
      const next = new URLSearchParams(window.location.search);

      const set = (name: string, value: string | null) => {
        if (value) next.set(param(name), value);
        else next.delete(param(name));
      };

      set('q', query.trim() || null);
      set('sort', sort?.key ?? null);
      set('dir', sort ? (sort.direction === 'descending' ? 'desc' : 'asc') : null);
      set('size', pageSize === SMALLEST_PAGE ? null : String(pageSize));
      set('page', page > 1 ? String(page) : null);

      // Every filter this table knows about, so one being cleared takes its
      // parameter with it rather than leaving a stale one behind.
      for (const key of [
        ...filters.map((filter) => filter.key),
        ...columns.filter((column) => column.filter).map((column) => columnFilterKey(column.key)),
      ]) {
        set(`f.${key}`, chosen[key]?.length ? chosen[key].join(',') : null);
      }

      return next.toString();
    };

    /* The mount pass is skipped, and only the mount pass: a table opened from a
       plain link must not rewrite the address it arrived with. Ordered this way
       deliberately — with the equality check first, a table that mounted with an
       empty address returned before this flag was ever set, so the *first*
       filter anybody chose was swallowed and only the second written. Which is
       exactly how a reload lost the choice somebody had made. */
    if (!settled.current) {
      settled.current = true;
      return;
    }
    if (build() === search) return;

    // Held back a moment. The rows narrow on the keystroke — that is local
    // state and costs nothing — but a navigation per character typed into the
    // search box is a re-render per character, and the address bar is no use
    // to anyone mid-word anyway.
    const writing = setTimeout(() => {
      const fresh = build();
      router.replace(fresh ? `${pathname}?${fresh}` : pathname, { scroll: false });
    }, URL_SETTLE_MS);
    return () => clearTimeout(writing);
    // `param` and the two lists are derived from props that do not change for
    // the life of a table; the state above is what this follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort, pageSize, page, chosen, search, pathname, router]);

  const totalPages = sizeNow === 'all' ? 1 : Math.max(1, Math.ceil(count / sizeNow));
  // Clamped rather than reset by effect: a poll that shrinks the row count
  // (or a filter that does) must not strand the reader on a page that no
  // longer exists, but re-deriving it on every render costs nothing extra.
  const clampedPage = Math.min(page, totalPages);
  // The server answers for the page it was asked; a filter that shrank the list
  // has to ask again for one that still exists.
  useEffect(() => {
    if (remote && remote.total > 0 && page > totalPages) setPage(totalPages);
  }, [remote, page, totalPages]);
  const pageRows = useMemo(() => {
    if (remote || !pager || sizeNow === 'all') return shown;
    const start = (clampedPage - 1) * sizeNow;
    return shown.slice(start, start + sizeNow);
  }, [shown, sizeNow, clampedPage, pager, remote]);
  // The smallest real page size (20): at or under it there is only ever one
  // page, so the numbered buttons and Prev/Next are controls that cannot do
  // anything. The bar itself still draws -- same edge, same "Show", same
  // range -- because a short list that loses the bar entirely reads as a
  // different table with a different pager rather than as the same one with
  // nothing to page through.
  const smallestPageSize =
    PAGE_SIZES.find((size): size is Exclude<PageSize, 'all'> => typeof size === 'number') ?? 20;
  const showPager = pager && count > 0;
  const showPageButtons = pager && count > smallestPageSize;
  // A second bar under the table is only worth its height once the first one
  // has been scrolled past, and half a page of rows is where that starts. The
  // last page holding a single row was printing the same sentence twice around
  // one line of data.
  const showBottomPager =
    showPager &&
    (pagerAt === 'bottom' || (showPageButtons && pageRows.length >= smallestPageSize / 2));

  /* "Show 20" rather than a strip of four buttons: how many rows a page holds
     is one choice out of four, which is what a select is, and four buttons
     beside four page numbers read as eight ways to page. */
  const sizePick = (
    <label className="pager-size">
      <span>Show</span>
      <Select
        size="sm"
        aria-label="Rows per page"
        value={String(sizeNow)}
        onChange={(value) => {
          setPage(1);
          setPageSize(value === 'all' ? 'all' : (Number(value) as PageSize));
        }}
        options={PAGE_SIZES.filter((size) => !remote || size !== 'all').map((size) => ({
          value: String(size),
          label: size === 'all' ? 'All' : String(size),
        }))}
      />
    </label>
  );

  /* Where in the list this page falls, not how many rows it holds: "21–40 of
     74" is the sentence a reader checks a page number against. */
  const rangeSpan =
    sizeNow === 'all' ? (
      <PageRange from={1} to={count} total={count} />
    ) : (
      <PageRange
        from={(clampedPage - 1) * sizeNow + 1}
        to={Math.min(clampedPage * sizeNow, count)}
        total={count}
      />
    );

  const navButtons = (
    <PageButtons page={clampedPage} totalPages={totalPages} onPage={setPage} />
  );

  /* One row, drawn on the table's own edge — top and bottom, the same words in
     both places. Three tracks, the outer two equal, so the numbers sit at the
     middle of the table rather than at the end of whatever is on the left. */
  const pagerRow = (where: 'top' | 'bottom') => (
    <div className={`pager pager-${where}`}>
      {/* The range on one side, how many rows a page holds on the other, the
          numbers in the middle: both on the left, they were longer than the
          space the centred numbers left them and the two ran into each other
          on a narrow table. */}
      <div className="pager-shown">{rangeSpan}</div>
      {showPageButtons ? navButtons : <span />}
      <div className="pager-shown pager-shown-end">{sizePick}</div>
    </div>
  );

  return (
    <>
      {hasToolbar && (
        <div className="toolbar">
          {searchable && (
            /* Our own ✕ rather than the one `type="search"` gives: WebKit
               draws it, Firefox does not, and neither can be styled — a
               control this useful cannot be there on one colleague's machine
               and missing on another's. */
            <div className="toolbar-search">
              <input
                type="search"
                // The small variants, both here and on the filters below: a
                // toolbar belongs to the table under it and reads at the rows'
                // density, not at a form's. Full-height controls over a
                // twenty-six pixel row were the tallest thing on the screen
                // carrying one word.
                className="field field-sm"
                placeholder={searchLabel}
                aria-label={searchLabel}
                value={query}
                onChange={(event) => {
                  setPage(1);
                  setQuery(event.target.value);
                }}
              />
              {query && (
                <button
                  type="button"
                  className="toolbar-search-x"
                  aria-label="Clear search"
                  onClick={() => {
                    setPage(1);
                    setQuery('');
                  }}
                >
                  {'✕'}
                </button>
              )}
            </div>
          )}
          {filters.map((filter) =>
            filter.multiple ? (
              <Select
                key={filter.key}
                multiple
                size="sm"
                aria-label={filter.label}
                value={chosen[filter.key] ?? []}
                // The same words as the row that is ticked while nothing is
                // filtered. "Type" over a list whose first line reads "All
                // types" made the reader check whether the two were the same
                // thing.
                placeholder={allLabel(filter)}
                manyLabel={plural(filter)}
                onChange={(values) => {
                  setPage(1);
                  setChosen((current) => ({ ...current, [filter.key]: values }));
                }}
                options={[{ value: '', label: allLabel(filter) }, ...filter.options]}
              />
            ) : (
              <Select
                key={filter.key}
                size="sm"
                aria-label={filter.label}
                value={chosen[filter.key]?.[0] ?? ''}
                placeholder={unsetLabel(filter)}
                onChange={(value) => {
                  setPage(1);
                  setChosen((current) => ({ ...current, [filter.key]: value ? [value] : [] }));
                }}
                // The first option clears the filter rather than being a value
                // of it, and it says what it brings back: unset reads "All
                // statuses", not the bare noun "Status", which a reader takes
                // for a heading rather than for the current choice.
                options={[{ value: '', label: unsetLabel(filter) }, ...filter.options]}
              />
            ),
          )}
          {summaryNode && <span className="toolbar-summary">{summaryNode}</span>}
          {resetKeeps && (changedFromDefault || hiddenCols.join() !== quietKeys(columns).join()) && (
            <button
              type="button"
              className="btn btn-sm toolbar-reset"
              title="Clear every filter, the search, the sort and the column choice"
              onClick={() => {
                try {
                  window.localStorage.removeItem(storeKey);
                } catch {
                  /* Nothing remembered to forget. */
                }
                const kept = new URLSearchParams();
                for (const key of resetKeeps) {
                  const value = params.get(key);
                  if (value !== null) kept.set(key, value);
                }
                // A full load, not a route change: every filter here holds its value in state, read once from the address.
                window.location.assign(`${pathname}${kept.size ? `?${kept.toString()}` : ''}`);
              }}
            >
              {'Reset'}
            </button>
          )}
          {canPickColumns && (
            /* The same control the filters beside it use, so the toolbar has
               one shape: ticked is shown. What is unticked is still on the
               row's own page — this hides a lane, it does not drop data.

               At the far right, where every product that has this control puts
               it: it is about the table rather than about the rows, so it does
               not belong in the row of questions the reader is asking. */
            <span className="toolbar-cols">
            <Select
              multiple
              size="sm"
              aria-label="Which columns to show"
              placeholder="Columns"
              manyLabel="columns"
              resetLabel="Show every column"
              /* The first column is listed, ticked and locked: a reader looking
                 for it in the list read its absence as the table having lost it. */
              value={[...(anchor ? [anchor.key] : []), ...optional.filter((one) => !hiddenCols.includes(one.key)).map((one) => one.key)]}
              onChange={(picked) => {
                const keys = picked.filter((key) => key !== anchor?.key);
                keepColumns(keys.length > 0 ? keys : optional.map((one) => one.key));
              }}
              options={[...(anchor ? [anchor] : []), ...optional].map((one) => ({
                value: one.key,
                label: typeof one.label === 'string' ? one.label : one.key,
                disabled: one === anchor,
              }))}
            />
            </span>
          )}
          {actions && <span className="toolbar-actions">{actions}</span>}
        </div>
      )}

      {/* The pager belongs to the table, so it is drawn on the card's own
          edges rather than floating above and below it. The scroll box is
          inside: a wide table scrolls sideways under a row that stays put,
          which is what happened to the pager when the card itself scrolled
          (Artur, 2026-09-18). */}
      <div className={`card table-pane${loading ? ' is-loading' : ''}`} aria-busy={loading}>
        {showPager && pagerAt === 'both' && pagerRow('top')}
        <div className="table-scrollbar" ref={wide.bar} hidden={!wide.width} aria-hidden="true">
          <div style={{ width: wide.width }} />
        </div>
        <div className={`table-wrap${wide.width ? ' is-wide' : ''}`} ref={wide.wrap}>
        <table className={`table${fixedLayout ? ' table-fixed' : ''}`}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {shownColumns.map((column) => {
                const sortable = Boolean(column.value);
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    /* Which column this is, said in the markup: a screen too
                       narrow for every lane drops one by name in CSS, and
                       nth-child counting broke the moment a lane moved. */
                    data-col={column.key}
                    data-align={column.align === 'right' ? 'right' : undefined}
                    className={sortable ? 'sortable' : undefined}
                    style={{
                      width: column.width,
                      textAlign: column.align === 'right' ? 'right' : undefined,
                    }}
                    title={column.hint}
                    aria-sort={sortable ? (active ? sort!.direction : 'none') : undefined}
                  >
                    {/* The name is the control, not the cell. A whole header
                        that sorts is a pointer over empty space, and over the
                        filter beneath it; a button is also the one version of
                        this a keyboard can reach. */}
                    {sortable ? (
                      <button
                        type="button"
                        className="th-label th-sort"
                        onClick={() =>
                          setSort((current) => {
                            if (current?.key !== column.key) {
                              return { key: column.key, direction: 'ascending' };
                            }
                            // Third click: back to unsorted, not straight
                            // ascending again: a reader cycling through a
                            // column's order gets a way out of it.
                            if (current.direction === 'ascending') {
                              return { key: column.key, direction: 'descending' };
                            }
                            return null;
                          })
                        }
                      >
                        {column.label}
                      </button>
                    ) : (
                      <span className="th-label">{column.label}</span>
                    )}
                    {column.filter && (
                      <span className="th-filter">
                        <Select
                          size="sm"
                          multiple
                          aria-label={`Filter by ${typeof column.label === 'string' ? column.label : column.key}`}
                          value={chosen[columnFilterKey(column.key)] ?? []}
                          placeholder={columnAllLabel(column)}
                          manyLabel="chosen"
                          onChange={(values) => {
                            setPage(1);
                            setChosen((current) => ({
                              ...current,
                              [columnFilterKey(column.key)]: values,
                            }));
                          }}
                          options={column.filter.options}
                        />
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              // What belongs under this row rather than beside it: an account's
              // own models, an order's lines. Returned null by default, so a
              // table that has no such thing pays nothing for the idea.
              const beneath = under?.(row);
              return (
                <Fragment key={rowKey(row)}>
                  <tr
                    className={[onRowClick ? 'clickable' : '', rowClassName?.(row) ?? '']
                      .filter(Boolean)
                      .join(' ') || undefined}
                    /* A row opens what it is about, but a control inside it
                       does its own job and nothing else: without this, picking
                       a value from a dropdown in a cell also opened the row's
                       panel behind the open list. */
                    onClick={
                      onRowClick
                        ? (event) => {
                            if (!(event.target as HTMLElement).closest('button, a, input, select, textarea, label')) {
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {shownColumns.map((column) => (
                      <td
                        key={column.key}
                        data-col={column.key}
                        className={column.clip ? 'cell-clip' : undefined}
                        style={{ textAlign: column.align === 'right' ? 'right' : undefined }}
                      >
                        {column.render ? column.render(row) : formatted(column.value?.(row))}
                      </td>
                    ))}
                  </tr>
                  {beneath && (
                    <tr className="row-under">
                      <td colSpan={shownColumns.length}>{beneath}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {shown.length === 0 && loading && <p className="empty">{'Loading…'}</p>}
        {shown.length === 0 && !loading && (
          <div className="empty">
            {/* Two different situations, said differently. A filtered table that
                looks like an empty one sends people to support. */}
            {filtering ? (
              <>
                <strong>Nothing matched</strong>
                {/* The typed words, in full and wrapped: the search box cuts a
                    long one off, so this is the only place a reader can see
                    what was actually asked for. No button beside it — the
                    search box above is still on screen with its own ✕, and two
                    ways back from one empty table is one too many. */}
                {query.trim() ? (
                  <p className="empty-hint empty-query">
                    <q>{query.trim()}</q>
                  </p>
                ) : (
                  <p className="empty-hint">{noMatch}</p>
                )}
                {/* One way back, rather than asking somebody to find which of
                    five controls is the one still narrowing the list. */}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setPage(1);
                    setQuery('');
                    setChosen({});
                  }}
                >
                  {'Show everything again'}
                </button>
              </>
            ) : (
              empty
            )}
          </div>
        )}
        </div>
        {showBottomPager && pagerRow('bottom')}
      </div>

    </>
  );
}

/**
 * Which page numbers to draw: first, last, current, and one on each side of
 * it; everything else collapses to an ellipsis. At 122 rows and page size
 * 20 that's seven pages and every one of them fits with room to spare; the
 * window keeps the row this way when a bigger table pushes that into the
 * hundreds instead of growing a button per page.
 */
/**
 * The widest `pageWindow` ever gets: first, last, the current page and one
 * either side, with an ellipsis standing in for each stretch between them.
 */
const PAGE_SLOTS = 7;

/** The window, padded either side so the row is always PAGE_SLOTS wide and
 *  the arrows at its ends never move. */
/** The page numbers on their own, so a card that pages its own rows does not
 *  grow a second pager. No First/Last: `pageWindow` keeps page 1 and the last
 *  page in the numbered list, both one click away from anywhere. */
export function PageButtons({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
}) {
  return (
    <div
      className="pager-pages"
      role="group"
      aria-label="Page"
      /* Every slot as wide as the widest page number there can be, so the row
         is the same length on page 1 as on page 50 and the arrows at its ends
         stay under the pointer that is clicking them. */
      style={{ '--pager-digits': String(totalPages).length } as React.CSSProperties}
    >
      <button
        type="button"
        className="pager-step"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        {'« prev'}
      </button>
      {/* Padded to a fixed number of slots, all of one width: `pageWindow`
          returns between two and seven entries depending on where in the list
          you are, so the row grew and shrank as you paged. */}
      {paddedWindow(pageWindow(page, totalPages), totalPages).map((entry, index) =>
        entry === 'ellipsis' ? (
          <span key={`e${index}`} className="pager-ellipsis" aria-hidden="true">
            {'…'}
          </span>
        ) : entry === 'gap' ? (
          <span key={`g${index}`} className="pager-slot" aria-hidden="true" />
        ) : (
          <button
            key={entry}
            type="button"
            className={`pager-page${entry === page ? ' is-on' : ''}`}
            aria-current={entry === page ? 'page' : undefined}
            onClick={() => onPage(entry)}
          >
            {entry}
          </button>
        ),
      )}
      <button
        type="button"
        className="pager-step"
        aria-label="Next page"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        {'next »'}
      </button>
    </div>
  );
}

function paddedWindow(
  window: Array<number | 'ellipsis'>,
  totalPages: number,
): Array<number | 'ellipsis' | 'gap'> {
  /* Padded to the slots this list can ever use, not to seven always: a list of
     two pages never grows a third number, so five blank slots between « prev
     and next » were holding space for pages that cannot exist. What the
     padding is for is a row that does not change width *as you page*, and the
     number of pages does not change while you do. */
  const missing = Math.max(0, Math.min(PAGE_SLOTS, totalPages) - window.length);
  const before = Math.floor(missing / 2);
  return [
    ...Array<'gap'>(before).fill('gap'),
    ...window,
    ...Array<'gap'>(missing - before).fill('gap'),
  ];
}

export function pageWindow(current: number, total: number): Array<number | 'ellipsis'> {
  // Widened until the row is full rather than fixed at one neighbour either
  // side: the seven slots are drawn either way, and "1 … 9 10 11 12" spends
  // them on pages somebody might actually go to. Stops at PAGE_SLOTS, so the
  // row still never changes width.
  let best = draw(1, current, total);
  for (let radius = 2; radius <= total; radius += 1) {
    const wider = draw(radius, current, total);
    if (wider.length > PAGE_SLOTS) break;
    best = wider;
  }
  return best;
}

/** First, last, and `radius` pages either side of the current one, with an
 *  ellipsis for each stretch left out. */
function draw(radius: number, current: number, total: number): Array<number | 'ellipsis'> {
  const kept = new Set<number>([1, total]);
  for (let p = current - radius; p <= current + radius; p += 1) kept.add(p);
  const pages = [...kept].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result: Array<number | 'ellipsis'> = [];
  let previous = 0;
  for (const p of pages) {
    if (previous && p - previous > 1) result.push('ellipsis');
    result.push(p);
    previous = p;
  }
  return result;
}

/** Plural of what a filter picks: its own word where it gave one, since an
 * appended s is wrong for every label ending in one ("Billing status"). */
function plural<T>(filter: Filter<T>): string {
  return filter.manyLabel ?? `${filter.label.toLowerCase()}s`;
}

/** The row that clears a several-choice filter. Named after what it brings
 * back rather than "All": a reader who has ticked two of five is looking for
 * the way out, and "All types" is that sentence. */
function allLabel<T>(filter: Filter<T>): string {
  return filter.allLabel ?? `All ${plural(filter)}`;
}

/** What a one-choice filter says while nothing is chosen. A screen whose label
 * is already that sentence ("Any state", "Anyone") keeps its own words; a bare
 * noun becomes one, because "Status" over a list of statuses reads as a
 * heading rather than as the current choice. */
function unsetLabel<T>(filter: Filter<T>): string {
  return filter.allLabel ?? (BARE_NOUN.test(filter.label) ? `All ${plural(filter)}` : filter.label);
}

/** One word, no "Any"/"All"/"Every" in front of it. */
const BARE_NOUN = /^(?!(any|all|every)\b)\w+$/i;

/** The unset words for a column's own filter: the header above it names the
 * thing, the control says how much of it is showing. A bare "All" under five
 * headers is five controls that look identical when nothing is chosen. */
function columnAllLabel<T>(column: Column<T>): string {
  if (column.filter?.allLabel) return column.filter.allLabel;
  const name = (typeof column.label === 'string' ? column.label : column.key).toLowerCase();
  return `All ${name}${name.endsWith('s') ? '' : 's'}`;
}

/** Namespaced, so a column filter and a toolbar filter of the same name cannot
 * quietly overwrite each other's choice in the one record that holds both. */
function columnFilterKey(key: string): string {
  return `column:${key}`;
}

function compare(a: unknown, b: unknown): number {
  // Absent sorts last in either direction rather than counting as zero or as
  // the empty string, both of which put "not yet known" in among real values.
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  const an = a instanceof Date ? a.getTime() : a;
  const bn = b instanceof Date ? b.getTime() : b;

  if (typeof an === 'number' && typeof bn === 'number') return an - bn;
  return String(an).localeCompare(String(bn), undefined, { numeric: true, sensitivity: 'base' });
}

function formatted(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toLocaleString();
  return String(value);
}

/* A table wider than its card scrolls sideways, and its own scrollbar sits
   under the last row, a page away from where the reader is looking. This one
   is drawn above the header and moves with it; zero width means it fits. */
const OVERFLOW_SLACK_PX = 12;

function useWideScroll() {
  const wrap = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const box = wrap.current;
    const top = bar.current;
    if (!box || !top) return;
    /* A few pixels over is rounding, from percent widths and borders, not a table
       that does not fit: the bar showed on screens where every column was there. */
    const measure = () => setWidth(box.scrollWidth - box.clientWidth > OVERFLOW_SLACK_PX ? box.scrollWidth : 0);
    const table = box.querySelector('table');
    const watch = new ResizeObserver(measure);
    watch.observe(box);
    if (table) watch.observe(table);
    measure();
    /* Each side copies the other only when it differs, so the two listeners
       do not bounce one scroll back and forth. */
    const fromBox = () => { if (top.scrollLeft !== box.scrollLeft) top.scrollLeft = box.scrollLeft; };
    const fromBar = () => { if (box.scrollLeft !== top.scrollLeft) box.scrollLeft = top.scrollLeft; };
    box.addEventListener('scroll', fromBox, { passive: true });
    top.addEventListener('scroll', fromBar, { passive: true });
    return () => {
      watch.disconnect();
      box.removeEventListener('scroll', fromBox);
      top.removeEventListener('scroll', fromBar);
    };
  }, []);
  return { wrap, bar, width };
}

function quietKeys<T>(columns: ReadonlyArray<Column<T>>): string[] {
  return columns.filter((one) => one.quiet).map((one) => one.key);
}
